import {
    ApolloClient,
    InMemoryCache,
    createHttpLink,
    GraphQLRequest,
    ApolloLink,
    Operation,
    NormalizedCacheObject,
    FetchResult,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { Observable } from '@apollo/client/utilities';
import * as Sentry from '@sentry/react';
import { notifications } from '@mantine/notifications';
import { GraphQLError } from 'graphql/error';

import { REFRESH_TOKENS } from './mutations/auth';
import { useAuthStore } from '../stores/auth.store';

const uri = import.meta.env.VITE_APOLLO_URI;

const FUNCTIONS_NOT_TO_RETRY = [
    'RefreshTokens',
    'Login',
    'Logout',
    'RequestPasswordReset',
    'RequestLoginOtp',
    'VerifyLoginOTP',
];

let client: ApolloClient<NormalizedCacheObject>;

type TokenResult = { accessToken: string; refreshToken: string } | null;

let refreshTokenPromise: Promise<TokenResult> | null = null;

function isAccessTokenValid(): boolean {
    const token = localStorage.getItem('accessToken');
    if (!token) return false;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.exp * 1000 > Date.now();
    } catch {
        return false;
    }
}

async function executeRefreshMutation(): Promise<{
    accessToken: string;
    refreshToken: string;
}> {
    const { data } = await client.mutate<{
        refreshTokens: { accessToken: string; refreshToken: string };
    }>({ mutation: REFRESH_TOKENS });

    if (!data) {
        throw new Error('Failed to refresh tokens');
    }

    const { accessToken, refreshToken } = data.refreshTokens;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    return { accessToken, refreshToken };
}

function isRefreshOrLogoutRequest(operation: GraphQLRequest) {
    return (
        operation.operationName === 'RefreshTokens' ||
        operation.operationName === 'Logout'
    );
}

// Make token retrieval async to match the pattern
async function returnTokenDependingOnOperation(operation: GraphQLRequest) {
    if (isRefreshOrLogoutRequest(operation)) {
        return localStorage.getItem('refreshToken') || '';
    }
    return localStorage.getItem('accessToken') || '';
}

// Make authLink async
const authLink = setContext(async (operation, { headers }) => {
    const accessToken = await returnTokenDependingOnOperation(operation);
    return {
        headers: {
            ...headers,
            Authorization: `Bearer ${accessToken}`,
        },
    };
});

function getErrorCode(err: {
    extensions?: { code?: string };
    code?: string;
}): string | undefined {
    return err?.extensions?.code ?? err?.code;
}

function shouldNotRetry(operation: GraphQLRequest) {
    const { operationName } = operation;

    if (!operationName) {
        return true;
    }

    return FUNCTIONS_NOT_TO_RETRY.includes(operationName);
}

const MAX_REFRESH_RETRIES = 3;
const REFRESH_RETRY_BASE_DELAY_MS = 500;

async function refreshWithRetry(
    attempt = 0,
): Promise<{ accessToken: string; refreshToken: string }> {
    try {
        return await executeRefreshMutation();
    } catch (error: any) {
        const isAuthRejection = error?.graphQLErrors?.some(
            (e: any) => getErrorCode(e) === 'UNAUTHENTICATED',
        );
        if (isAuthRejection) {
            throw error;
        }
        if (attempt >= MAX_REFRESH_RETRIES - 1) {
            throw error;
        }
        const delay = REFRESH_RETRY_BASE_DELAY_MS * 2 ** attempt;
        await new Promise<void>(resolve => {
            setTimeout(resolve, delay);
        });
        return refreshWithRetry(attempt + 1);
    }
}

export const refreshTokens = async (options?: {
    force?: boolean;
}): Promise<TokenResult> => {
    if (!options?.force) {
        const { isLoggedIn } = useAuthStore.getState();
        if (!isLoggedIn) {
            return null;
        }
    }

    if (refreshTokenPromise) {
        return refreshTokenPromise;
    }

    refreshTokenPromise = (async () => {
        try {
            if (typeof navigator?.locks?.request === 'function') {
                return await navigator.locks.request(
                    'cashbuild-token-refresh',
                    async () => {
                        if (!options?.force && isAccessTokenValid()) {
                            return {
                                accessToken:
                                    localStorage.getItem('accessToken')!,
                                refreshToken:
                                    localStorage.getItem('refreshToken')!,
                            };
                        }
                        return refreshWithRetry();
                    },
                );
            }

            return await refreshWithRetry();
        } catch (error) {
            const { setLoggedIn, setUserId, setStandInForUserIds } =
                useAuthStore.getState();
            setLoggedIn(false);
            setUserId();
            setStandInForUserIds([]);
            throw new Error('Failed to refresh tokens');
        } finally {
            refreshTokenPromise = null;
        }
    })();

    return refreshTokenPromise;
};

function logSentryError(
    { path, extensions, locations, message }: GraphQLError,
    operation: Operation,
) {
    const errorInfo = {
        type: 'GraphQL Error',
        operation: operation.operationName,
        path: path?.join('.'),
        locations: JSON.stringify(locations),
        extensions,
    };

    Sentry.withScope(scope => {
        scope.setExtra('graphql', errorInfo);
        const definition = operation.query.definitions[0];
        if (definition.kind === 'OperationDefinition') {
            scope.setTag('operationType', definition.operation);
        }
        Sentry.captureException(new Error(message));
    });

    console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
    );
}

// Helper function to handle auth refresh outside the loop
const createAuthRefreshHandler = (
    observer: any,
    operation: any,
    forward: any,
) => {
    const { getState } = useAuthStore;
    const { setLoggedIn, setUserId, setStandInForUserIds } = getState();

    const { pathname, search } = window.location;

    return async () => {
        try {
            const refreshResult = await refreshTokens({ force: true });

            // Check if refresh was skipped (user logged out)
            if (refreshResult === null) {
                // Don't retry the operation, just complete the observer
                observer.complete();
                return;
            }

            const { accessToken, refreshToken } = refreshResult;
            if (!accessToken) {
                throw new GraphQLError('No access token');
            }
            if (!refreshToken) {
                throw new GraphQLError('No refresh token');
            }
            const subscriber = {
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
            };
            forward(operation).subscribe(subscriber);
        } catch (error) {
            console.error(
                '[Auth Refresh] Error refreshing tokens in GraphQL Client. Error:',
                error,
            );

            // Check if this is a logout scenario vs expired session
            const { isLoggedIn: currentLoggedIn } = getState();
            const hasTokens =
                localStorage.getItem('accessToken') ||
                localStorage.getItem('refreshToken');

            if (!currentLoggedIn || !hasTokens) {
                // Don't show session expired notification if user is already logged out
            } else {
                notifications.show({
                    title: 'Session expired',
                    message: 'Your session has expired. Please log in again.',
                    color: 'red',
                });
            }

            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');

            if (pathname !== '/auth/login') {
                window.location.href = `/auth/login?redirectUrl=${encodeURIComponent(
                    pathname + search,
                )}`;
            }

            Sentry.setUser(null);

            setLoggedIn(false);
            setUserId();
            setStandInForUserIds([]);

            await client.resetStore();
            observer.error(error);
        }
    };
};

const errorLink = onError(
    ({ graphQLErrors, networkError, operation, forward }) => {
        if (graphQLErrors) {
            // eslint-disable-next-line no-restricted-syntax
            for (const err of graphQLErrors) {
                switch (getErrorCode(err)) {
                    case 'UNAUTHENTICATED':
                        if (
                            isRefreshOrLogoutRequest(operation) ||
                            shouldNotRetry(operation)
                        ) {
                            return;
                        }
                        // eslint-disable-next-line consistent-return
                        return new Observable<FetchResult<Record<string, any>>>(
                            observer => {
                                const handleAuthRefresh =
                                    createAuthRefreshHandler(
                                        observer,
                                        operation,
                                        forward,
                                    );

                                handleAuthRefresh();
                            },
                        );
                    default:
                        logSentryError(err as GraphQLError, operation);
                }
            }
        }
        if (networkError) {
            console.error('[Network error]:', networkError);
            Sentry.withScope(scope => {
                scope.setExtra('networkError', networkError);
                const definition = operation.query.definitions[0];
                if (definition.kind === 'OperationDefinition') {
                    scope.setTag('operationType', definition.operation);
                }
                Sentry.captureException(networkError);
            });
        }
    },
);

const createClient = () => {
    const httpLink = createHttpLink({
        uri,
    });

    client = new ApolloClient({
        link: ApolloLink.from([errorLink, authLink, httpLink]),

        uri,

        cache: new InMemoryCache({
            typePolicies: {
                Store: {
                    fields: {
                        productAvailability: {
                            keyArgs: ['sku'],
                        },
                    },
                },
            },
        }),
    });

    return client;
};

const initialClient = createClient();

export { createClient };

export default initialClient;
